import { useNavigate, useParams } from 'react-router-dom';
import {
    CreateOrderFromMedSheet,
    OrderSourceEnum,
    OrderTemplate,
} from '../../models/PatientModels';
import RoundedButton from '../RoundedButton';
import {
    OrderContainer,
    OrderFooter,
    OrderItem,
    OrderItemInline,
    OrderNotFound,
    OrderTitle,
} from './OrderCard';
import { OrderTab, OrderTabGrid } from './OrderTab';
import {
    useCreateOrderFromMedSheetMutation,
    useGetLatestEMRUpdateQuery,
    useGetOrderTemplatesQuery,
} from '../../features/patient/patientService';
import { useEffect } from 'react';
import useOrderToast from '../../hooks/useOrderToast';
import moment from 'moment';

const MedSheets = ({
    medSheet,
    handleSubmit,
}: {
    medSheet: OrderTemplate;
    handleSubmit: () => void;
}) => {
    return (
        <OrderContainer title={<OrderTitle>{medSheet.drugName}</OrderTitle>}>
            <OrderItemInline
                label="Source:"
                value={OrderSourceEnum[medSheet.source].toUpperCase()}
                className="text-medium-blue"
            />
            <OrderItem
                label="Drug Format"
                value={medSheet.drugFormat ?? 'N/A'}
            />
            <OrderFooter>
                {
                    <RoundedButton
                        onClick={handleSubmit}
                        className={
                            'fw-semibold bg-light-blue text-white fw-semibold'
                        }
                    >
                        Review & Submit
                    </RoundedButton>
                }
            </OrderFooter>
        </OrderContainer>
    );
};

const MedSheetsTab = ({setIsErrorModalOpen}:{setIsErrorModalOpen:Function}) => {
    const { id: patientId = '' } = useParams();
    const navigate = useNavigate();

    const { toast } = useOrderToast();

    const { data: medSheets, isLoading } = useGetOrderTemplatesQuery({
        patientId: patientId,
        source: OrderSourceEnum.EMR,
    });
    const [createOrderFromMedSheet, { isLoading: isCreatingOrder, error }] =
        useCreateOrderFromMedSheetMutation();

    useEffect(() => {
        if (!error) return;

        /*
        let message =
            'Review and correct the Med Sheet details in the EMR or create the Med Order via NaviScript.';

        if (typeof error === 'object' && 'message' in error) {
            message = `${error.message}. ${message}`;
        }
        toast(message, 'error');
        */

        setIsErrorModalOpen(true);
    }, [error]);

    const handleSubmit = async ({
        medSheetId,
        patientId,
    }: CreateOrderFromMedSheet) => {
        const response = await createOrderFromMedSheet({
            medSheetId,
            patientId,
        });

        if (typeof response === 'object' && 'data' in response) {
            const orderId = response.data.id;
            navigate(`/patient/${patientId}/pending-order/${orderId}`);
        }
    };

    const { data: latestEMRUpdate } = useGetLatestEMRUpdateQuery({
        patientId: patientId,
    });

    const parsedData =
        typeof latestEMRUpdate === 'string'
            ? JSON.parse(latestEMRUpdate)
            : latestEMRUpdate;

    const formattedDate = parsedData?.latestSyncDate
        ? moment(new Date(parsedData.latestSyncDate)).format('M/D/YYYY HH:mm')
        : 'N/A';

    return (
        <OrderTab isLoading={isLoading || isCreatingOrder}>
            <div className="fw-bold">
                Note:{' '}
                <span className="fw-light">
                    The CURRENT TREATMENT / MEDICATION / DME LIST was last
                    updated on {formattedDate}.
                </span>
            </div>
            <OrderTabGrid>
                {medSheets &&
                    medSheets.map((medSheet) => (
                        <MedSheets
                            key={medSheet.id}
                            medSheet={medSheet}
                            handleSubmit={() => {
                                handleSubmit({
                                    medSheetId: medSheet.id,
                                    patientId,
                                });
                            }}
                        />
                    ))}
            </OrderTabGrid>
            {!isLoading && (!medSheets || medSheets.length === 0) && (
                <OrderNotFound>
                    There are no med sheets to be displayed.
                </OrderNotFound>
            )}
        </OrderTab>
    );
};

export default MedSheetsTab;
